"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import {
  CheckIcon,
  ClipboardDocumentIcon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";

export function DivorceHeroMedia() {
  const containerRef = useRef(null);
  const mediaRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const media = mediaRef.current;
    if (!container || !media) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    let animationFrame;

    const updatePosition = () => {
      animationFrame = undefined;
      const bounds = container.getBoundingClientRect();
      const travelled = Math.min(Math.max(-bounds.top, 0), bounds.height);
      media.style.transform = `translate3d(0, ${travelled * 0.09}px, 0) scale(1.06)`;
    };

    const requestUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(updatePosition);
    };

    updatePosition();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="absolute inset-0 -z-20 overflow-hidden"
    >
      <div
        ref={mediaRef}
        className="absolute -inset-y-[8%] inset-x-0 origin-center will-change-transform"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/divorce-appraisal-condo-interior.png')",
          }}
        />
        <video
          autoPlay
          loop
          muted
          playsInline
          poster="/divorce-appraisal-condo-interior.png"
          className="absolute inset-0 h-full w-full object-cover object-center opacity-20 mix-blend-screen dark:opacity-25 motion-reduce:hidden"
        >
          <source src="/alpha_appraisals_room.mp4" type="video/mp4" />
        </video>
      </div>
    </div>
  );
}

export function DivorceReveal({
  children,
  className = "",
  delay = 0,
  as: Tag = "div",
  ...props
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(true);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }

    const bounds = element.getBoundingClientRect();
    if (bounds.top < window.innerHeight * 0.92 && bounds.bottom > 0) {
      setShown(true);
      return undefined;
    }

    setShown(false);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShown(true);
        observer.disconnect();
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      {...props}
      className={`transition-all duration-700 ease-out motion-reduce:transition-none ${
        shown ? "translate-y-0 opacity-100" : "translate-y-7 opacity-0"
      } ${className}`}
    >
      {children}
    </Tag>
  );
}

export function DivorceStickyBar() {
  const [visible, setVisible] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const copyResetRef = useRef(null);
  const email = "info@alphaappraisals.ca";

  useEffect(() => {
    const answerStrip = document.getElementById("divorce-answer-strip");
    if (!answerStrip) return undefined;

    const updateVisibility = () => {
      setVisible(answerStrip.getBoundingClientRect().bottom < 0);
    };

    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    return () => window.removeEventListener("scroll", updateVisibility);
  }, []);

  useEffect(
    () => () => {
      if (copyResetRef.current) window.clearTimeout(copyResetRef.current);
    },
    []
  );

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = email;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      textArea.remove();
    }

    setEmailCopied(true);
    if (copyResetRef.current) window.clearTimeout(copyResetRef.current);
    copyResetRef.current = window.setTimeout(() => setEmailCopied(false), 2200);
  };

  return (
    <aside
      aria-label="Speak with designated appraiser Mushtaq Khan"
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-[60] bg-white/95 px-3 py-2.5 shadow-[0_-14px_40px_rgba(15,23,42,0.14)] backdrop-blur-xl transition duration-300 sm:px-4 dark:bg-[linear-gradient(90deg,rgba(4,11,24,.98),rgba(8,23,49,.97),rgba(4,11,24,.98))] dark:shadow-[0_-14px_40px_rgba(0,0,0,0.5)] motion-reduce:transition-none ${
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-full opacity-0"
      }`}
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 sm:flex-nowrap">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <Image
            src="/mushtaq_khan_alpha_appraisals.webp"
            alt=""
            width={48}
            height={48}
            className="h-10 w-10 flex-none rounded-full border border-yellow-500/70 object-cover shadow-[0_0_18px_rgba(37,99,235,.2)]"
          />
          <div className="flex min-w-0 flex-1 items-center gap-2 font-gantari">
            <p className="hidden whitespace-nowrap text-sm font-semibold text-yellow-700 lg:block dark:text-yellow-300">
              Speak with a designated appraiser
            </p>
            <span
              aria-hidden="true"
              className="hidden h-5 w-px bg-blue-200 lg:block dark:bg-blue-300/25"
            />
            <p className="truncate text-sm font-semibold text-slate-950 dark:text-white">
              Mushtaq Khan,{" "}
              <span className="font-medium text-blue-700 dark:text-blue-300">
                CRA, P.App.
              </span>
            </p>
          </div>
          <a
            href="https://www.aicanada.ca/appraisers/910564"
            target="_blank"
            rel="noreferrer"
            aria-label="Verify Mushtaq Khan's AIC credentials"
            title="Verify AIC credentials"
            className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-slate-100 transition hover:bg-blue-50 dark:bg-white/[0.07] dark:hover:bg-white/[0.12]"
          >
            <Image
              src="/aic-logo-square.png"
              alt=""
              width={24}
              height={24}
              className="h-5 w-5 rounded-sm bg-white object-contain"
            />
          </a>
        </div>

        <div className="flex w-full min-w-0 flex-none items-center justify-end gap-3 rounded-xl bg-slate-50/80 px-3 py-2 font-gantari text-xs font-semibold text-slate-700 ring-1 ring-blue-100 sm:w-auto sm:bg-transparent sm:px-0 sm:py-0 sm:ring-0 sm:text-sm dark:bg-white/[0.04] dark:text-slate-200 dark:ring-blue-300/10 dark:sm:bg-transparent">
          <a
            href="tel:+14164502336"
            aria-label="Call Mushtaq Khan at 416 450 2336"
            title="Call (416) 450-2336"
            className="inline-flex flex-none items-center gap-1.5 text-yellow-700 transition hover:text-yellow-600 dark:text-yellow-300 dark:hover:text-yellow-200"
          >
            <PhoneIcon className="h-4 w-4" aria-hidden="true" />
            <span>(416) 450-2336</span>
          </a>
          <span
            aria-hidden="true"
            className="h-5 w-px bg-blue-200 dark:bg-blue-300/15"
          />
          <a
            href={`mailto:${email}`}
            aria-label={`Email Mushtaq Khan at ${email}`}
            title={email}
            className="inline-flex min-w-0 items-center gap-1.5 text-blue-700 transition hover:text-blue-900 dark:text-blue-100 dark:hover:text-white"
          >
            <EnvelopeIcon className="h-4 w-4 flex-none" aria-hidden="true" />
            <span className="truncate">{email}</span>
          </a>
          <button
            type="button"
            onClick={copyEmail}
            aria-label={emailCopied ? "Email address copied" : `Copy ${email}`}
            title={emailCopied ? "Email copied" : "Copy email address"}
            className={`inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg transition ${
              emailCopied
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.14] dark:text-emerald-300"
                : "bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-800 dark:bg-white/[0.07] dark:text-slate-300 dark:hover:bg-white/[0.12] dark:hover:text-white"
            }`}
          >
            {emailCopied ? (
              <CheckIcon className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ClipboardDocumentIcon
                className="h-4 w-4"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </div>
    </aside>
  );
}
